import { Injectable, NgZone, inject } from '@angular/core';
import { EventBusService, LogService, environment } from '@ups/xplat/core';
import { IEmployeeInfo, userFullName } from '@ups/xplat/api/dto';
import { debounceTime } from 'rxjs';
import { getAppVersionInfo } from '@ups/xplat/web/utils';
import {
  buildRynReplayMaskingPolicy,
  createRynReplaySessionId,
  IRynReplayMaskingPolicy,
  IRynReplaySessionIdStore,
  isPayloadRecordingAllowedForUrl,
  resolveRynReplayEnvironmentLabel,
  resolveRynReplaySessionId,
  rynReplaySessionUrl,
  RynReplayEventProperties,
  RYN_REPLAY_RECORDER_BACKEND,
} from '@ups/xplat/base/ryn-replay';
import { isFocusWithinHeavyElement } from './ryn-replay-focus-pause';
import { isHeavyElementPresent } from './ryn-replay-presence-pause';
import {
  MutationBurstPauseOptions,
  startMutationBurstWatch,
} from './ryn-replay-mutation-burst';
import {
  RYN_REPLAY_GRID_SNAPSHOTS_CONFIG,
  focusPauseSelectorsFromGridSnapshots,
  presencePauseSelectorsFromGridSnapshots,
} from './ryn-replay-grid-snapshots.config';

/**
 * sessionStorage key for the persisted replay session id (#30109).
 *
 * sessionStorage is scoped per-origin and per-tab: it survives a reload, a
 * direct URL entry, and the Auth0 login redirect (which returns to the same
 * origin in the same tab), and dies when the tab closes. That lifetime is
 * exactly one "visit", which is the unit a single session should span, and it
 * is why the WEB store is the one that decides what a visit is (see
 * `resolveRynReplaySessionId`).
 */
const RYN_REPLAY_SESSION_ID_STORAGE_KEY = 'ups.rynReplay.sessionId';

/**
 * Per-tab session-id store.
 *
 * Every access is guarded because this runs during recorder construction, and a
 * throw here would take out the provider graph of every app that opted in.
 * sessionStorage can throw outright in private mode, with storage disabled, or
 * inside a sandboxed iframe; a blocked store degrades to a per-load id, not a
 * crash.
 */
const webRynReplaySessionIdStore: IRynReplaySessionIdStore = {
  read: () =>
    globalThis.sessionStorage?.getItem(RYN_REPLAY_SESSION_ID_STORAGE_KEY) ??
    undefined,
  write: (sessionId) =>
    globalThis.sessionStorage?.setItem(
      RYN_REPLAY_SESSION_ID_STORAGE_KEY,
      sessionId
    ),
};

const uniqueSelectors = (selectors: string[]): string[] | undefined => {
  const out = [
    ...new Set(selectors.map((s) => s.trim()).filter((s) => s.length > 0)),
  ];
  return out.length ? out : undefined;
};

/**
 * Ryn Replay session recorder (ADO #30070), the in-house replacement for
 * WebSmartlookService. It keeps that service's public surface so the in-app
 * help desk and Halo ITSM modals, and core.module wiring, are unaffected.
 *
 * Gating is fail-closed: with no `environment.rynReplay.enabled` the whole
 * service is a silent no-op. No script load, no request, no log line.
 *
 * Session ids are generated client-side so `sessionUrl()` can answer
 * synchronously the moment recording starts, which is what the help-desk modal
 * needs when it stamps a replay link onto a ticket.
 */
@Injectable({
  providedIn: 'root',
})
export class RynReplayRecorderService {
  eventBus = inject(EventBusService);
  log = inject(LogService);
  private backend = inject(RYN_REPLAY_RECORDER_BACKEND);
  /**
   * Optional so unit tests that construct the service without a full Angular
   * platform still work. In a real app NgZone is always present; recorder
   * timers and rrweb emit MUST run outside it or every 100ms burst tick (and
   * every rrweb callback) also runs change detection, which is the opposite of
   * the non-blocking promise and shows up as Chrome `setTimeout`/`setInterval`
   * violations.
   */
  private ngZone = inject(NgZone, { optional: true });
  private readonly gridSnapshotsConfig = inject(
    RYN_REPLAY_GRID_SNAPSHOTS_CONFIG,
    { optional: true }
  );

  /** True once recording has started. Mirrors WebSmartlookService.init. */
  init = false;

  private sessionId?: string;
  private policy?: IRynReplayMaskingPolicy;
  private anonymized = false;

  constructor() {
    const config = environment.rynReplay;
    if (!config?.enabled) {
      // No config for this app: total no-op.
      return;
    }

    // rrweb emit, the burst 100ms interval, and the flush timer are all
    // setTimeout/setInterval. Inside NgZone each tick also runs Angular
    // change detection. That is how a "count-only" watcher still produced a
    // stream of Chrome `setTimeout` / `setInterval` violations. The identity
    // EventBus hooks stay in this same block: they only stamp recorder state.
    const start = () => this.startCapture(config);
    if (this.ngZone) {
      this.ngZone.runOutsideAngular(start);
    } else {
      start();
    }
  }

  private startCapture(
    config: NonNullable<typeof environment.rynReplay>
  ): void {
    this.policy = buildRynReplayMaskingPolicy(config);
    this.sessionId = resolveRynReplaySessionId(webRynReplaySessionIdStore);
    const versionInfo = getAppVersionInfo();

    // Wire auto-pause guards BEFORE backend.start(). start() dynamically imports
    // rrweb and may attach on a cached chunk in the same turn; registering the
    // burst MutationObserver first is what lets the hard-ceiling path pause
    // before rrweb's observer for later deliveries (#31398 ladder step 1).
    // Presence pause must also be live before start so a cold-load onto a heavy
    // page host pauses as soon as Angular mounts it.
    // Presence/focus pause: env selectors plus any hosts registered via
    // provideRynReplayGridSnapshots() so app.module is the single opt-in list.
    const presenceSelectors = uniqueSelectors([
      ...(config.pauseWhilePresentSelectors ?? []),
      ...presencePauseSelectorsFromGridSnapshots(this.gridSnapshotsConfig),
    ]);
    const focusSelectors = uniqueSelectors([
      ...(config.pauseOnFocusSelectors ?? []),
      ...focusPauseSelectorsFromGridSnapshots(this.gridSnapshotsConfig),
    ]);
    this.wirePresencePausing(presenceSelectors);
    this.wireFocusPausing(focusSelectors);
    this.wireMutationBurstPausing(config.mutationBurstPause);

    this.backend.start({
      sessionId: this.sessionId,
      policy: this.policy,
      appVersion: versionInfo?.versionName ?? 'local',
      buildNumber: versionInfo?.buildNumber ?? 'local',
      environment: resolveRynReplayEnvironmentLabel(
        environment.environment_name,
        environment.production
      ),
      onSessionRotation: () => {
        // Persist the new segment so #30109 continuity and #31331 idle split
        // compose: a reload after rotation stays on this id, not the pre-idle one.
        this.sessionId = createRynReplaySessionId();
        webRynReplaySessionIdStore.write(this.sessionId);
        return this.sessionId;
      },
    });
    this.init = true;

    this.eventBus
      .observe(this.eventBus.types.profileUpdated)
      .pipe(debounceTime(300))
      .subscribe((info: IEmployeeInfo) => {
        // EITHER id means we have someone. Gating on Auth0UserId alone was
        // #30113. Auth0UserId is a COLUMN on the security user row, not a token
        // claim, so an Entra-only or newly provisioned user has nothing there
        // while UserId, the Guid the server actually stores and indexes, is
        // sitting in the same payload. Those users went down the anonymize()
        // arm, which is PERMANENT, so their sessions recorded irreversibly
        // anonymous and no later profile event could undo it.
        //
        // Worth knowing which way this cuts: anyone who ever had an Auth0
        // identity keeps that column populated and was unaffected even when
        // signing in through Entra. It bit Entra-only and newly provisioned
        // users only. The player requires an Entra session, so the estate is
        // moving toward exactly the population this silenced.
        if (info?.Auth0UserId || info?.UserId) {
          this.setUser(info);
          this.log.debug('Ryn Replay session:', this.sessionUrl());
        } else {
          // Neither id: genuinely signed out, which is the case the original
          // branch was written for. SmartLook parity is preserved here.
          this.anonymize();
        }
      });

    // Navbar / AuthFacade emit this BEFORE Auth0/Entra redirect. Empty
    // profileUpdated never fires on that path, so without this hook AC5
    // (anonymize on logout) would lose the race to navigation (#30073).
    this.eventBus.observe(this.eventBus.types.logout).subscribe(() => {
      this.anonymize();
      void this.backend.flush(true);
    });
  }

  /**
   * Active automatic-pause reasons. Paused while any guard (presence, focus,
   * mutation burst) holds a reason; resumes only when the last clears. Without
   * the shared set the guards fight: one resuming while the other still wants
   * the pause re-emits a FullSnapshot into a still-heavy DOM and re-arms the
   * storm.
   */
  private readonly autoPauseReasons = new Set<string>();

  /**
   * Pause capture for the whole time a heavy page host is mounted (#31398).
   * Stronger than focus pause: the Equipment Usage initial grid render storms
   * before any cell is focused, so focus-only pause still freezes the tab on
   * entry. A count-only MutationObserver watches for configured hosts (e.g.
   * `ups-equipment-usage-page`); presence adds the shared `presence` auto-pause
   * reason, absence clears it. Coalesced to one check per microtask so a dense
   * Kendo paint does not run querySelector on every childList record. No-op when
   * nothing is configured or there is no document / MutationObserver (SSR).
   */
  private wirePresencePausing(heavySelectors?: string[]): void {
    if (
      !heavySelectors?.length ||
      typeof document === 'undefined' ||
      typeof MutationObserver === 'undefined'
    ) {
      return;
    }

    let scheduled = false;
    const sync = () => {
      this.setAutoPauseReason(
        'presence',
        isHeavyElementPresent(document, heavySelectors)
      );
    };
    const scheduleSync = () => {
      if (scheduled) {
        return;
      }
      scheduled = true;
      Promise.resolve().then(() => {
        scheduled = false;
        sync();
      });
    };

    // Already on a heavy page (cold load / late recorder start): pause now.
    sync();

    const observer = new MutationObserver(scheduleSync);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Pause capture while keyboard focus is inside a heavy element (#31398).
   * pause()/resume() detach and re-attach the rrweb listeners, so on the dense
   * Equipment Usage Kendo grid the recorder stops serializing the per-keystroke
   * DOM mutation storm on the main thread while the user is editing, then
   * resumes on blur. resume() re-emits a fresh FullSnapshot, so the replay keeps
   * the whole screen before, a short gap only while actively typing, and the
   * grid's final values after.
   *
   * Focus is tracked on the DOM, not the router, so merely viewing the page
   * still records fully; capture only pauses once the user focuses into the
   * grid, which is what actually triggers the storm. The relatedTarget check
   * keeps focus moving cell-to-cell inside the same heavy element from resuming,
   * which would otherwise emit a FullSnapshot on every keystroke. No-op when
   * nothing is configured or there is no document (SSR / non-browser).
   * Prefer `pauseWhilePresentSelectors` when the whole page visit must be paused
   * (initial grid render); keep this for edit-only gaps on lighter hosts.
   */
  private wireFocusPausing(heavySelectors?: string[]): void {
    if (!heavySelectors?.length || typeof document === 'undefined') {
      return;
    }
    let pausedForFocus = false;
    document.addEventListener(
      'focusin',
      (event: FocusEvent) => {
        if (
          !pausedForFocus &&
          isFocusWithinHeavyElement(event.target, heavySelectors)
        ) {
          pausedForFocus = true;
          this.setAutoPauseReason('focus', true);
        }
      },
      true
    );
    document.addEventListener(
      'focusout',
      (event: FocusEvent) => {
        if (!pausedForFocus) {
          return;
        }
        // relatedTarget is where focus is going. Still inside a heavy element
        // (cell to cell) means stay paused; only resume once focus has left
        // every heavy element, so resume()'s FullSnapshot fires once per edit
        // session rather than once per keystroke.
        if (!isFocusWithinHeavyElement(event.relatedTarget, heavySelectors)) {
          pausedForFocus = false;
          this.setAutoPauseReason('focus', false);
        }
      },
      true
    );
  }

  /**
   * Toggle one automatic-pause reason and drive the backend on the empty <->
   * non-empty transition, so the focus and mutation-burst guards compose instead
   * of fighting. On each real transition it emits a visible marker into the
   * replay stream (`ryn-replay:auto-paused` / `auto-resumed`, carrying the reason
   * and, for the burst guard, the window count and threshold) plus a debug line,
   * so a developer replaying the session sees why a gap is there and what tripped
   * it. The focus guard previously marked nothing; it does now too.
   */
  private setAutoPauseReason(
    reason: string,
    active: boolean,
    detail?: RynReplayEventProperties
  ): void {
    const wasPaused = this.autoPauseReasons.size > 0;
    if (active) {
      this.autoPauseReasons.add(reason);
    } else {
      this.autoPauseReasons.delete(reason);
    }
    const nowPaused = this.autoPauseReasons.size > 0;
    if (nowPaused === wasPaused) {
      return;
    }
    if (nowPaused) {
      this.pause();
      this.track('ryn-replay:auto-paused', { reason, ...detail });
      // Primitives, not an object: a copied DevTools dump collapses `{…}` and
      // the windowSum that tells you whether this was first paint or a storm
      // disappears.
      this.log.debug(
        'Ryn Replay auto-paused:',
        reason,
        `windowSum=${String(detail?.['windowSum'] ?? '')}`,
        `threshold=${String(detail?.['threshold'] ?? '')}`
      );
    } else {
      this.resume();
      this.track('ryn-replay:auto-resumed', { reason, ...detail });
      this.log.debug(
        'Ryn Replay auto-resumed:',
        reason,
        `windowSum=${String(detail?.['windowSum'] ?? '')}`,
        `threshold=${String(detail?.['threshold'] ?? '')}`
      );
    }
  }

  /**
   * Centralized rapid-DOM-mutation-burst auto-pause (#31398 follow-up). Pauses
   * capture during a mutation storm on ANY heavy page in ANY replay-enabled app,
   * with no per-page selector list. Default ON, kill switch
   * (`mutationBurstPause.enabled === false`) and tunable thresholds. The observer
   * and decision logic live in `ryn-replay-mutation-burst`; this only routes a
   * decision to the shared pause-reason set and stamps the marker.
   */
  private wireMutationBurstPausing(config?: MutationBurstPauseOptions): void {
    if (config?.enabled === false) {
      return;
    }
    startMutationBurstWatch(config, (decision, resolved) => {
      this.setAutoPauseReason('burst', decision.action === 'pause', {
        windowSum: decision.windowSum,
        threshold: resolved.burstThreshold,
      });
    });
  }

  /**
   * Legacy name kept for parity with WebSmartlookService.setUser.
   */
  setUser(info: IEmployeeInfo) {
    this.identify(
      info?.Auth0UserId,
      userFullName(info),
      info?.Data?.udUPSEmail ?? info?.Data?.Email,
      info?.Data?.HRRef,
      // IEmployeeInfo.UserId is the Software Security user Guid, not a second
      // Auth0 id. The userProfile endpoint resolves the caller's
      // http://security.universalplant.com/user-id claim to a security user row
      // and returns that row's UserId, so this is the same Guid the APIs mean by
      // GetSecurityUserId(). This is the one the server stores and indexes.
      info?.UserId
    );
  }

  /**
   * Stamp session metadata for the signed-in user. Ignored once the session has
   * been anonymized, so a late profile event cannot re-attach an identity.
   */
  identify(
    auth0UserId?: string,
    name?: string,
    email?: string,
    hrRef?: string | number,
    securityUserId?: string
  ) {
    // Either id is enough, and requiring auth0UserId here was the SECOND half of
    // #30113. Even on the paths that did reach this method with a securityUserId
    // present, this guard threw the whole identity away. Both halves had to move
    // or the fix would have looked done and changed nothing.
    //
    // The parameter is now optional because callers pass `info?.Auth0UserId`,
    // which has always been able to be undefined. The old `string` annotation
    // was describing a guarantee the caller never made.
    if (!this.init || this.anonymized || (!auth0UserId && !securityUserId)) {
      return;
    }
    const versionInfo = getAppVersionInfo();
    this.backend.setIdentity({
      auth0UserId,
      securityUserId,
      name,
      email,
      hrRef,
      versionTag: versionInfo?.versionName ?? 'local',
      buildNumber: versionInfo?.buildNumber ?? 'local',
    });
  }

  /**
   * Stop associating any identity with this session, permanently. Capture
   * itself continues, which matches the SmartLook behavior on a signed-out
   * profile event.
   */
  anonymize() {
    if (!this.init || this.anonymized) {
      return;
    }
    this.anonymized = true;
    this.backend.clearIdentity();
  }

  /**
   * Synchronous. Returns undefined when this app is not recording.
   *
   * Always ABSOLUTE. The load-bearing consumer is a replay link stamped onto a
   * Halo ITSM or help-desk ticket, which is then opened from the ticketing
   * system or an email rather than from inside the app that wrote it, so a
   * relative path would resolve against the wrong host. This matches the
   * SmartLook behavior it replaces (`window.smartlook.playUrl` was absolute).
   */
  sessionUrl(): string | undefined {
    return rynReplaySessionUrl(
      this.sessionId,
      environment.rynReplay?.playerBaseUrl,
      globalThis.location?.origin
    );
  }

  pause() {
    if (!this.init) {
      return;
    }
    this.backend.pause();
  }

  resume() {
    if (!this.init) {
      return;
    }
    this.backend.resume();
  }

  /** Legacy name kept for parity with WebSmartlookService.trackEvent. */
  trackEvent(name: string, properties?: RynReplayEventProperties) {
    this.track(name, properties);
  }

  track(name: string, properties?: RynReplayEventProperties) {
    if (!this.init) {
      return;
    }
    this.backend.addEvent(name, properties);
  }

  /**
   * Payload allowlist enforcement point, used by the network capture layer
   * before it retains any request or response body.
   */
  isPayloadRecordingAllowed(url: string): boolean {
    if (!this.policy) {
      return false;
    }
    return isPayloadRecordingAllowedForUrl(this.policy, url);
  }
}
